import {
  Box,
  Button,
  Collapse,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Archive,
  CaretDown,
  CaretRight,
  NotePencil,
  PencilSimpleLine,
  Plus,
  Trash
} from '@phosphor-icons/react';
import { useState } from 'react';
import DotsThreeOutlineGrayIcon from '../../../../assets/icons/DotsThreeOutlineGrayIcon.svg';
import DotsThreeOutlineWhiteIcon from '../../../../assets/icons/DotsThreeOutlineWhiteIcon.svg';
import { AuthService } from '../../../../service/AuthService';
import { UserService } from '../../../../service/UserService';
import { useObjectStore } from '../../../../store/storeObjects';
import { User } from '../../../../util/Types/User';
import IconButtonTooltip from '../../../UI/IconButtonTooltip/IconButtonTooltip';
import ObjectModal from './ObjectModal';

type Props = {
  authUser: User;
  sidebarOpen: boolean;
  setAuthUser: (user: User) => void;
  handleCloseSideBarMobile: () => void;
  spaceId?: string;
  modifySpaceObject?: (space: any) => void;
  space?: any;
};

const FluxObjects = ({
  authUser,
  sidebarOpen,
  setAuthUser,
  handleCloseSideBarMobile,
  spaceId,
  modifySpaceObject,
  space,
}: Props) => {
  const [objectsCollapsed, setObjectsCollapsed] = useState(false);
  const [isObjectModalOpen, setIsObjectModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [clickedObjectId, setClickedObjectId] = useState<string | null>(null);
  const { setObjectUpdate, resetObjectUpdate, objects, setObjects } = useObjectStore(state => state);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string,
  ) => {
    setAnchorEl(event.currentTarget);
    setClickedObjectId(id === clickedObjectId ? null : id);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setClickedObjectId(null);
  };

  const onObjectDelete = async (id: string) => {
    try {
      const result = window.confirm(
        'Are you sure you want to delete the object?',
      );

      if (!result) return;

      if (spaceId && modifySpaceObject && space?.id) {
        const newObjects = space.objects.filter((object: any) => object.id !== id);
        modifySpaceObject({
          ...space,
          objects: newObjects,
        });
        setObjects(newObjects, spaceId);
      } else {
        const newObjects = authUser?.objects.filter((object: any) => object.id !== id);
        const res = await UserService.updateUser({
          objects: newObjects,
        });
        const userRes = await AuthService.getMe();
        setAuthUser(userRes?.data);
        setObjects(newObjects, 'user');
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Box
        className={`layouts_sidebar_menu--btn align-items-center `}
        onClick={() => setObjectsCollapsed(!objectsCollapsed)}
        sx={{
          pt: '4px',
          display: 'flex',
          justifyContent: !sidebarOpen ? 'center' : 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          color: authUser?.theme === 'dark' ? '#D0D5DD' : '#667085',
          paddingLeft: '13px',
          // width: '14.9rem',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Box
          className="menu menu--on-hover"
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            paddingRight: '7px',
            paddingBottom: '4px',
            '&:hover': {
              backgroundColor:
                authUser?.theme === 'dark' ? '#101828' : '#E7F0FF',
              borderRadius: '4px',
            },
          }}
        >
          <Box sx={{ display: 'flex', columnGap: '8px', paddingLeft: '5px' }}>
            <Archive
              className="active-menu"
              color={authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'}
              size={20}
              weight="regular"
              style={{ alignSelf: 'center' }}
            />
            {sidebarOpen && (
              <>
                <Typography
                  className="text-capitalize active-menu"
                  sx={{
                    color: authUser?.theme === 'dark' ? '#D0D5DD' : '#475467',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                  }}
                >
                  Objects
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ alignSelf: 'center', display: 'flex', gap: '8px' }}>
            <IconButtonTooltip
              title="Create Flux Object"
              onClick={event => {
                event.stopPropagation();
                resetObjectUpdate();
                setIsObjectModalOpen(true);
              }}
            >
              <Plus
                size={16}
                className="menu-icons active-menu"
                style={{
                  color: authUser?.theme === 'dark' ? '#D0D5DD' : '#667085',
                  cursor: 'pointer',
                }}
              />
            </IconButtonTooltip>
            {sidebarOpen && (
              <IconButtonTooltip title="Collapse">
                {!objectsCollapsed ? (
                  <CaretDown
                    className="active-menu"
                    size={16}
                    color={authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'}
                  />
                ) : (
                  <CaretRight
                    className="active-menu"
                    size={16}
                    color={authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'}
                  />
                )}
              </IconButtonTooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Collapse
        timeout={500}
        in={objectsCollapsed}
        sx={{
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            paddingLeft: sidebarOpen ? '44px' : '',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
          }}
        >
          {objects[spaceId || 'user']
            ?.filter((object: any) => !object.localObject)
            ?.map((object: any) => (
              <Box
                key={object.id}
                className="menu menu--on-hover"
                sx={{
                  maxWidth: '320px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  paddingLeft: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': {
                    backgroundColor:
                      authUser?.theme === 'dark' ? '#101828' : '#E7F0FF',
                    borderRadius: '4px',
                  },
                }}
              >
                <Tooltip title={object.name}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: authUser?.theme === 'dark' ? '#D0D5DD' : '#475467',
                      fontSize: '16px',
                      width: '13rem',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      margin: '0',
                      marginBottom: '5px',
                      '&:hover': {
                        color: '#3650DE',
                      },
                    }}
                    onClick={() => {
                      handleCloseSideBarMobile();
                      setObjectUpdate(object);
                      setIsObjectModalOpen(true);
                    }}
                  >
                    {object?.name}
                  </Typography>
                </Tooltip>

                <IconButton
                  size="medium"
                  key={object.id}
                  onClick={e => {
                    handleClick(e, object.id);
                  }}
                >
                  {authUser?.theme === 'dark' ? (
                    <DotsThreeOutlineWhiteIcon />
                  ) : (
                    <DotsThreeOutlineGrayIcon />
                  )}
                </IconButton>
                <Popover
                  open={clickedObjectId === object.id}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: '8px',
                      background:
                        authUser?.theme === 'dark' ? '#101828' : '#FFFF',
                    }}
                  >
                    <Button
                      variant="contained"
                      sx={{
                        width: '100%',
                        background: 'unset',
                        justifyContent: 'flex-start',
                        textTransform: 'unset',
                        fontSize: '16px',
                        boxShadow: 'unset',
                        px: '8px',
                        gap: '8px',
                        color: '#EAECF0',
                        ':hover': {
                          background:
                            authUser?.theme === 'dark' ? ' #1D2939' : '#EAECF0',
                          boxShadow: 'unset',
                        },
                      }}
                      onClick={() => {
                        setObjectUpdate({
                          isCreating: false,
                          name: object.name,
                          id: object.id,
                          sessions: object.sessions,
                          text: object.text,
                          localObject: object.localObject,
                          sessionEnabled: object.sessionEnabled,
                          flowId: object.flowId,
                        });
                        setIsObjectModalOpen(true);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        <PencilSimpleLine
                          size={20}
                          color={
                            authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'
                          }
                        />
                        <Typography
                          color={
                            authUser?.theme === 'dark' ? '#D0D5DD' : '#475467'
                          }
                        >
                          Rename
                        </Typography>
                      </Box>
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        width: '100%',
                        background: 'unset',
                        justifyContent: 'flex-start',
                        textTransform: 'unset',
                        fontSize: '16px',
                        boxShadow: 'unset',
                        px: '8px',
                        gap: '8px',
                        color: '#EAECF0',
                        ':hover': {
                          background:
                            authUser?.theme === 'dark' ? ' #1D2939' : '#EAECF0',
                          boxShadow: 'unset',
                        },
                      }}
                      onClick={() => {
                        setObjectUpdate({
                          isCreating: false,
                          name: object.name,
                          id: object.id,
                          sessions: object.sessions,
                          text: object.text,
                          localObject: object.localObject,
                          sessionEnabled: object.sessionEnabled,
                          flowId: object.flowId,
                        });
                        setIsObjectModalOpen(true);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        <NotePencil
                          size={20}
                          color={
                            authUser?.theme === 'dark' ? '#D0D5DD' : '#667085'
                          }
                        />
                        <Typography
                          color={
                            authUser?.theme === 'dark' ? '#D0D5DD' : '#475467'
                          }
                        >
                          Edit
                        </Typography>
                      </Box>
                    </Button>
                    <Button
                      variant="contained"
                      sx={{
                        width: '100%',
                        background: 'unset',
                        justifyContent: 'flex-start',
                        textTransform: 'unset',
                        fontSize: '16px',
                        boxShadow: 'unset',
                        color: '#b91c1c',
                        px: '8px',
                        gap: '8px',
                        ':hover': {
                          background:
                            authUser?.theme === 'dark' ? '#FFADB7' : '#fecdd3',
                          boxShadow: 'unset',
                        },
                      }}
                      onClick={() => {
                        onObjectDelete(object.id);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        <Trash size={20} color="#b91c1c" />
                        <Typography>Delete</Typography>
                      </Box>
                    </Button>
                  </Box>
                </Popover>
              </Box>
            ))}
        </div>
      </Collapse>

      <ObjectModal
        onHide={() => {
          setIsObjectModalOpen(false);
        }}
        spaceId={spaceId}
        show={isObjectModalOpen}
      />
    </>
  );
};

export default FluxObjects;